import React, { useState, useCallback, useEffect } from 'react'
import Head from 'next/head'
import styled, { css } from 'styled-components'
import * as R from 'ramda'
import { useRouter } from 'next/router'
import URI from 'urijs'

import { Swiper, SwiperSlide } from 'swiper/react'
import { BUTTON_NAME, COUPON_SCREEN_NAME, EVENT_ACTION } from 'tracking'
import { EventEmitter } from 'events'
import api from 'api'
import { tabletSize, mobileSize } from 'theme'
import { active, hidden, boxShadow } from './mixin'
import { useUserStore } from 'store/userStore'
import { useErrorStore } from 'store/errorStore'
import { useTrackingStore } from 'store/trackingStore'
import { CategoryLoader } from 'components/ContentLoaderV2'
import IconPrev from '/static/images/ic-prev.svg'
import IconNext from '/static/images/ic-next.svg'
const CategoryBar = ({ hiddenNavigationButtons, showCategoryBar = true }) => {
  const router = useRouter()
  const [noSwiping, setNoSwiping] = useState(false)
  const { accessToken: walletJwt } = useUserStore()

  const {
    loading,
    categories,
    activeCategoryId,
    activeCategoryName,
    onSelectedCategory,
  } = useCategory({
    router,
    setNoSwiping,
    walletJwt,
  })
  const { params, swiper, hiddenPrev, hiddenNext, getSwiper } = useSwiper({
    router,
  })

  return (
    <>
      <Head>
        <title>01優惠券 | 香港01</title>
        <meta
          property="og:title"
          content={`${activeCategoryName || '01優惠券'} | 香港01`}
        />
        <meta
          property="og:description"
          content={`即換最新最抵${activeCategoryName || ''}優惠券`}
        />
      </Head>
      <CategoryWrapper showCategoryBar={showCategoryBar}>
        {loading ? (
          <CategoryLoader />
        ) : (
          <CategoryBarInner>
            <CategorySwiper className={noSwiping ? 'swiper-no-swiping' : ''}>
              <Swiper {...params} onSwiper={getSwiper}>
                {categories.map((category) => (
                  <CategoryItem
                    key={category.id}
                    active={Number(
                      String(activeCategoryId) === String(category.id)
                    )}
                    onClick={() => {
                      onSelectedCategory(category)
                    }}
                    slot="wrapper-start"
                  >
                    {category.name}
                  </CategoryItem>
                ))}
              </Swiper>
              {!hiddenNavigationButtons && (
                <NavigationButtons
                  swiper={swiper}
                  hiddenPrev={hiddenPrev}
                  hiddenNext={hiddenNext}
                />
              )}
            </CategorySwiper>
          </CategoryBarInner>
        )}
      </CategoryWrapper>
    </>
  )
}

// TODO: Put into use hook file, and do a unit test
const useCategory = ({ router, setNoSwiping, walletJwt }) => {
  const { pathname, query = {} } = router || {}
  const { wrapErrorHandler } = useErrorStore()
  const { trackEvent, isInited } = useTrackingStore
  const { hasBindPhone } = useUserStore()

  const [loading, setLoading] = useState(false)
  const [activeCategoryId, setActiveCategoryId] = useState('')
  const [activeCategoryName, setActiveCategoryName] = useState('')
  const [categories, setCategories] = useState(
    pathname === '/mycoupon' ? myCouponCategories : defaultCategories
  )

  const loadCouponStatusCount = useCallback(async () => {
    await wrapErrorHandler(async () => {
      if (walletJwt && hasBindPhone()) {
        const { data: countData } = await api.getCouponStatusCount(walletJwt)
        setCategories(
          myCouponCategories.map((categoryItem) => {
            const categoryItemCount = R.pipe(
              R.prop('id'),
              R.toLower,
              R.split(','),
              R.map(R.concat(R.__, 'Num')),
              R.pick(R.__, countData),
              R.values,
              R.sum
            )(categoryItem)
            return {
              name: `${categoryItem.name}(${categoryItemCount})`,
              id: categoryItem.id,
            }
          })
        )
        emitter.emit(EVENT.UPDATE_SWIPER)
      }
    })
  }, [hasBindPhone, walletJwt, wrapErrorHandler])

  const reloadData = useCallback(async () => {
    setLoading(true)
    await wrapErrorHandler(async () => {
      const response = await api.getCategories()
      setCategories([
        ...defaultCategories,
        ...R.pathOr([], ['data', 'categories'], response),
      ])
      emitter.emit(EVENT.UPDATE_SWIPER)
    })
    setLoading(false)
  }, [wrapErrorHandler])

  useEffect(() => {
    if (pathname === '/skus') {
      setActiveCategoryId(query.categoryId || '')
      setActiveCategoryName(query.categoryName)
    } else {
      setActiveCategoryId(query.currentStatus || myCouponCategories[0].id)
    }
  }, [
    pathname,
    query.categoryId,
    query.categoryName,
    query.currentStatus,
    setActiveCategoryId,
  ])

  useEffect(() => {
    if (pathname === '/skus') {
      reloadData()
      setNoSwiping(false)
    } else {
      setTimeout(() => {
        setNoSwiping(true)
        loadCouponStatusCount()
        emitter.emit(EVENT.UPDATE_SWIPER)
      }, 0)
    }
  }, [pathname, reloadData, setNoSwiping, loadCouponStatusCount])

  const onSelectedCategory = useCallback(
    (category) => {
      const newUrl = URI(router.asPath)
        .query({
          ...(pathname === '/skus' &&
            category.id && {
              categoryId: category.id,
              categoryName: category.name,
            }),
          ...(pathname === '/mycoupon' && {
            currentStatus: category.id,
          }),
        })
        .toString()
      router.replace(newUrl)
      setActiveCategoryId(category.id)
      setActiveCategoryName(category.name)
      if (pathname === '/mycoupon') {
        loadCouponStatusCount()
      }
      const screenName = pathname.substr(1).toUpperCase()
      if (isInited) {
        trackEvent(EVENT_ACTION.CLICK, {
          screen: COUPON_SCREEN_NAME[screenName],
          tab: category.name,
          button: BUTTON_NAME.TAB,
        })
      }
    },
    [isInited, loadCouponStatusCount, pathname, router, trackEvent]
  )

  return {
    loading,
    categories,
    activeCategoryId,
    activeCategoryName,
    onSelectedCategory,
  }
}

// TODO: Put into hook folder and to unit test
const useSwiper = () => {
  const [swiper, setSwiper] = useState(null)
  const [hiddenPrev, setHiddenPrev] = useState(true)
  const [hiddenNext, setHiddenNext] = useState(true)
  const params = {
    slidesPerView: 'auto',
    spaceBetween: 0,
    slidesPerGroup: 3,
    freeMode: true,
    freeModeSticky: true,
    onProgress: useCallback((progress) => {
      emitter.emit(EVENT.PROGRESS, progress)
    }, []),
  }

  useEffect(() => {
    emitter.addListener(EVENT.PROGRESS, (progress) => {
      if (!swiper) {
        return
      }

      if (swiper.isBeginning && swiper.isEnd) {
        setHiddenPrev(true)
        setHiddenNext(true)
        return
      }

      R.is(Boolean, swiper.isBeginning) && setHiddenPrev(swiper.isBeginning)
      R.is(Boolean, swiper.isEnd) && setHiddenNext(swiper.isEnd)

      if (progress <= 0) {
        setHiddenPrev(true)
      }

      if (progress >= 1) {
        setHiddenNext(true)
      }
    })

    emitter.addListener(EVENT.UPDATE_SWIPER, () => {
      if (!swiper) {
        return
      }

      swiper.update()
    })

    return () => {
      emitter.removeAllListeners()
    }
  }, [swiper])

  const getSwiper = useCallback((swiper) => {
    R.is(Boolean, swiper.isBeginning) && setHiddenPrev(swiper.isBeginning)
    R.is(Boolean, swiper.isEnd) && setHiddenNext(swiper.isEnd)

    setSwiper(swiper)
  }, [])

  return {
    params,
    swiper,
    hiddenPrev,
    hiddenNext,
    getSwiper,
  }
}

const NavigationButtons = ({ hiddenPrev, hiddenNext, swiper }) => {
  const goPrev = useCallback(() => {
    if (swiper !== null) {
      swiper.slidePrev()
    }
  }, [swiper])

  const goNext = useCallback(() => {
    if (swiper !== null) {
      swiper.slideNext()
    }
  }, [swiper])

  return (
    <>
      <ButtonToSeeMore prev hidden={hiddenPrev} onClick={goPrev}>
        <IconPrev />
      </ButtonToSeeMore>
      <ButtonToSeeMore next hidden={hiddenNext} onClick={goNext}>
        <IconNext />
      </ButtonToSeeMore>
    </>
  )
}

const emitter = new EventEmitter()

const EVENT = {
  PROGRESS: 'PROGRESS',
  UPDATE_SWIPER: 'UPDATE_SWIPER',
}

const defaultCategories = [{ name: '全部', id: '' }]

const myCouponCategories = [
  { name: '未使用', id: 'REDEEMED' },
  { name: '已使用', id: 'USED' },
  { name: '已失效', id: 'WAIVED,EXPIRED,FROZEN' },
]

const CategoryWrapper = styled.div`
  display: ${(p) => (p.showCategoryBar ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background-color: ${(p) => p.theme.color.white};
  padding: 16px 0;
  ${boxShadow};

  .swiper-slide {
    width: auto;
  }

  @media ${mobileSize} {
    padding: 8px 0;
  }
`

const CategoryBarInner = styled.div`
  max-width: ${(p) => p.theme.desktopMaxWidth};
  padding: 0 24px;
  box-sizing: border-box;
  overflow: hidden;

  @media ${tabletSize} {
    padding: 0 16px;
  }

  @media ${mobileSize} {
    padding: 0 12px;
  }
`

const CategorySwiper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
`

const CategoryItem = styled(SwiperSlide)`
  display: inline-block;
  padding: 0;
  height: 32px;
  line-height: 32px;
  border-radius: 20px;
  cursor: pointer;
  user-select: none;
  font-size: 15px;
  font-weight: 400;
  color: ${(p) => p.theme.color.grey400};
  margin: 0 17px;
  flex-shrink: 0;

  ${(p) =>
    p.active &&
    css`
      ${active};
      cursor: default;
      background-color: ${(p) => p.theme.color.blue50};
      padding: 0 17px;
      margin: 0;
    `}
`

const ButtonToSeeMore = styled.div`
  width: 32px;
  height: 64px;
  cursor: pointer;
  flex-shrink: 0;
  position: absolute;
  display: flex;
  align-items: center;
  user-select: none;
  z-index: 1;

  ${(p) =>
    p.prev &&
    css`
      background-image: linear-gradient(
        to left,
        rgba(255, 255, 255, 0),
        ${(p) => p.theme.color.white} 50%
      );
      left: 0;
    `}

  ${(p) =>
    p.next &&
    css`
      justify-content: flex-end;
      background-image: linear-gradient(
        to left,
        ${(p) => p.theme.color.white} 50%,
        rgba(255, 255, 255, 0)
      );
      right: 0;
    `}

    ${(p) => p.hidden && hidden}
`

export default CategoryBar
